import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

/** Colunas leves para sync/reconcile — sem joins de reactions/members. */
export const CHAT_SYNC_MESSAGE_SELECT = `
  id,
  body,
  from_user,
  created_at,
  metadata,
  conversation_id,
  sent_by_member_id,
  delivery_status,
  provider_message_id,
  deleted_at,
  deleted_by_member_id,
  deleted_scope
`;

export type ServerMessageRow = {
  id: string;
  body?: string | null;
  from_user: string;
  created_at: string;
  metadata?: unknown;
  conversation_id?: string;
  sent_by_member_id?: string | null;
  delivery_status?: string | null;
  provider_message_id?: string | null;
  deleted_at?: string | null;
  deleted_by_member_id?: string | null;
  deleted_scope?: string | null;
};

type MetadataRecord = Record<string, unknown>;

export function mergeMessageMetadata(
  prev: unknown,
  next: unknown,
): unknown {
  if (!next || typeof next !== "object") return prev ?? next;
  if (!prev || typeof prev !== "object") return next;

  const prevObj = prev as MetadataRecord;
  const nextObj = next as MetadataRecord;
  const merged: MetadataRecord = { ...prevObj, ...nextObj };

  const prevMedia =
    prevObj.media && typeof prevObj.media === "object"
      ? (prevObj.media as MetadataRecord)
      : {};
  const nextMedia =
    nextObj.media && typeof nextObj.media === "object"
      ? (nextObj.media as MetadataRecord)
      : {};

  if (Object.keys(prevMedia).length > 0 || Object.keys(nextMedia).length > 0) {
    merged.media = { ...prevMedia, ...nextMedia };
  }

  return merged;
}

export const HUB_PREVIEW_MESSAGE_SOURCE = "hub_preview";

export function isHubPreviewMessageId(id: string | undefined): boolean {
  return typeof id === "string" && id.startsWith("hub-preview:");
}

export function buildHubPreviewMessageId(conversationId: string, lastMessageAt: string): string {
  return `hub-preview:${conversationId}:${lastMessageAt}`;
}

export function isHubPreviewMessage(message: {
  id?: string;
  metadata?: { source?: string } | null;
}): boolean {
  if (isHubPreviewMessageId(message.id)) return true;
  return message.metadata?.source === HUB_PREVIEW_MESSAGE_SOURCE;
}

/** Preview do read model (até 100 chars) vs corpo real da mensagem. */
export function hubPreviewMatchesOutboundBody(preview: string, body: string): boolean {
  const p = preview.trim();
  const b = body.trim();
  if (!p || !b) return false;
  if (b === p || b.startsWith(p) || p.startsWith(b)) return true;
  return b.slice(0, 100) === p.slice(0, 100);
}

export function removeSupersededHubPreviews<T extends {
  id?: string;
  role?: string;
  content?: string;
  created_at?: string;
  metadata?: { source?: string } | null;
}>(messages: T[], confirmed: T[]): T[] {
  const outboundConfirmed = confirmed.filter(
    (m) => m.role === "user" && m.id && !isHubPreviewMessage(m),
  );
  if (outboundConfirmed.length === 0) return messages;

  return messages.filter((message) => {
    if (!isHubPreviewMessage(message)) return true;
    return !outboundConfirmed.some(
      (real) =>
        real.created_at === message.created_at ||
        hubPreviewMatchesOutboundBody(message.content ?? "", real.content ?? ""),
    );
  });
}

export function isOutboundMessageStale(message: {
  id?: string;
  role?: string;
  delivery_status?: string | null;
  metadata?: { media?: { pending?: boolean }; source?: string } | null;
}): boolean {
  if (isHubPreviewMessage(message)) return false;
  if (message.role !== "user") return false;
  if (message.delivery_status === "pending") return true;
  if (message.metadata?.media?.pending === true) return true;
  return false;
}

export function collectStaleOutboundMessageIds(
  messagesByChannel: Record<string, Array<{ id?: string; role?: string; delivery_status?: string | null; metadata?: { media?: { pending?: boolean } } | null }>>,
  limit = 20,
): string[] {
  const ids: string[] = [];
  for (const messages of Object.values(messagesByChannel)) {
    for (const message of messages) {
      if (!message.id || !isOutboundMessageStale(message)) continue;
      ids.push(message.id);
      if (ids.length >= limit) return ids;
    }
  }
  return ids;
}

export function hasStaleOutboundMessages(
  messagesByChannel: Record<string, Array<{ id?: string; role?: string; delivery_status?: string | null; metadata?: { media?: { pending?: boolean } } | null }>>,
): boolean {
  return collectStaleOutboundMessageIds(messagesByChannel, 1).length > 0;
}

export function patchMessageFromServerRow<T extends {
  content?: string;
  metadata?: unknown;
  delivery_status?: string | null;
  provider_message_id?: string | null;
  deleted_at?: string | null;
  deleted_by_member_id?: string | null;
  deleted_scope?: string | null;
}>(
  existing: T,
  row: ServerMessageRow,
): T {
  return {
    ...existing,
    content: row.body ?? existing.content,
    metadata: mergeMessageMetadata(existing.metadata, row.metadata) as T["metadata"],
    delivery_status: row.delivery_status ?? existing.delivery_status,
    provider_message_id: row.provider_message_id ?? existing.provider_message_id,
    deleted_at: row.deleted_at ?? existing.deleted_at,
    deleted_by_member_id: row.deleted_by_member_id ?? existing.deleted_by_member_id,
    deleted_scope: row.deleted_scope ?? existing.deleted_scope,
  };
}

export type ChatMessageReaction = {
  reaction: string;
  from_user: string;
};

export type ChatMemberRef = {
  display_name: string | null;
};

/** Shape compatível com Message em ChatInterface. */
export type ChatMessage = {
  id?: string;
  role: "user" | "assistant";
  content: string;
  created_at?: string;
  metadata?: unknown;
  reactions?: ChatMessageReaction[];
  sent_by_member?: ChatMemberRef | null;
  sent_by_member_id?: string | null;
  delivery_status?: string | null;
  provider_message_id?: string | null;
  deleted_at?: string | null;
  deleted_by_member_id?: string | null;
  deleted_scope?: string | null;
  deleted_by_member?: ChatMemberRef | null;
};

/** Mensagens fantasma: curtidas IG salvas como body antes de message_reactions. */
export function isLegacyInstagramReactionMessage(metadata: unknown): boolean {
  if (!metadata || typeof metadata !== "object") return false;
  const meta = metadata as Record<string, unknown>;
  return meta.type === "reaction" && meta.normalized_event_type === "REACTION";
}

export function computeLastMessageAt(
  messages: Array<{ created_at?: string }>,
): string | null {
  if (messages.length === 0) return null;
  const latest = messages.reduce(
    (acc, message) =>
      message.created_at && message.created_at > acc ? message.created_at : acc,
    messages[0].created_at ?? "",
  );
  return latest || null;
}

export function serverRowToChatMessage(row: ServerMessageRow): ChatMessage {
  return {
    id: row.id,
    role: row.from_user === "assistant" ? "user" : "assistant",
    content: row.body || "",
    created_at: row.created_at,
    metadata: row.metadata,
    reactions: [],
    sent_by_member: null,
    sent_by_member_id: row.sent_by_member_id ?? null,
    delivery_status: row.delivery_status ?? null,
    provider_message_id: row.provider_message_id ?? null,
    deleted_at: row.deleted_at ?? null,
    deleted_by_member_id: row.deleted_by_member_id ?? null,
    deleted_scope: row.deleted_scope ?? null,
    deleted_by_member: null,
  };
}

export type GroupRowsByChannelOptions = {
  skipLegacyInstagramReactions?: boolean;
};

export function groupRowsByChannel(
  rows: ServerMessageRow[],
  convChannelMap: Map<string, string>,
  options?: GroupRowsByChannelOptions,
): Record<string, ChatMessage[]> {
  const skipLegacy = options?.skipLegacyInstagramReactions ?? true;
  const grouped: Record<string, ChatMessage[]> = {};

  for (const row of rows) {
    const convId = row.conversation_id;
    if (!convId) continue;
    const channel = convChannelMap.get(convId);
    if (!channel) continue;
    if (skipLegacy && isLegacyInstagramReactionMessage(row.metadata)) continue;

    if (!grouped[channel]) grouped[channel] = [];
    grouped[channel].push(serverRowToChatMessage(row));
  }

  return grouped;
}

export type MessageEnrichment = {
  reactionsByMessageId: Map<string, ChatMessageReaction[]>;
  membersById: Map<string, ChatMemberRef>;
};

export function applyEnrichmentToMessages(
  grouped: Record<string, ChatMessage[]>,
  enrichment: MessageEnrichment,
): Record<string, ChatMessage[]> {
  const result: Record<string, ChatMessage[]> = {};

  for (const [channel, messages] of Object.entries(grouped)) {
    result[channel] = messages.map((message) => enrichSingleMessage(message, enrichment));
  }

  return result;
}

function enrichSingleMessage(message: ChatMessage, enrichment: MessageEnrichment): ChatMessage {
  if (!message.id) return message;

  const reactions = enrichment.reactionsByMessageId.get(message.id);
  const sentByMember = message.sent_by_member_id
    ? enrichment.membersById.get(message.sent_by_member_id) ?? message.sent_by_member
    : message.sent_by_member;
  const deletedByMember = message.deleted_by_member_id
    ? enrichment.membersById.get(message.deleted_by_member_id) ?? message.deleted_by_member
    : message.deleted_by_member;

  if (!reactions && sentByMember === message.sent_by_member && deletedByMember === message.deleted_by_member) {
    return message;
  }

  return {
    ...message,
    reactions: reactions ?? message.reactions ?? [],
    sent_by_member: sentByMember ?? message.sent_by_member ?? null,
    deleted_by_member: deletedByMember ?? message.deleted_by_member ?? null,
  };
}

/**
 * Orçamento de queries por abertura de chat (por usuário, após otimização P2):
 * - Cache fresh HIT: 0 queries (mensagens + activity logs)
 * - Cache stale HIT: 1 messages + 2 activity (background, debounced) + enrichment chunked
 * - MISS: 1 messages + reconcile (0–1 sync) + 2 activity + enrichment chunked
 * Enrichment: ceil(N/50) queries em message_reactions + 1 em workspace_members (se houver ids).
 */
const ENRICHMENT_MESSAGE_ID_CHUNK = 50;

async function fetchReactionsForMessageIds(
  supabase: SupabaseClient<Database>,
  messageIds: string[],
): Promise<Map<string, ChatMessageReaction[]>> {
  const reactionsByMessageId = new Map<string, ChatMessageReaction[]>();
  if (messageIds.length === 0) return reactionsByMessageId;

  for (let i = 0; i < messageIds.length; i += ENRICHMENT_MESSAGE_ID_CHUNK) {
    const chunk = messageIds.slice(i, i + ENRICHMENT_MESSAGE_ID_CHUNK);
    const { data, error } = await supabase
      .from("message_reactions")
      .select("message_id, reaction, from_user")
      .in("message_id", chunk);

    if (error) {
      console.error("❌ Erro ao carregar reações:", error);
      continue;
    }

    for (const row of data ?? []) {
      if (!row.message_id) continue;
      const existing = reactionsByMessageId.get(row.message_id) ?? [];
      existing.push({ reaction: row.reaction, from_user: row.from_user });
      reactionsByMessageId.set(row.message_id, existing);
    }
  }

  return reactionsByMessageId;
}

export async function fetchMessageEnrichment(
  supabase: SupabaseClient<Database>,
  messageIds: string[],
  memberIds: string[],
): Promise<MessageEnrichment> {
  const uniqueMessageIds = [...new Set(messageIds.filter(Boolean))];
  const uniqueMemberIds = [...new Set(memberIds.filter(Boolean))];

  if (uniqueMessageIds.length === 0) {
    return { reactionsByMessageId: new Map(), membersById: new Map() };
  }

  const [reactionsByMessageId, membersById] = await Promise.all([
    fetchReactionsForMessageIds(supabase, uniqueMessageIds),
    (async () => {
      const map = new Map<string, ChatMemberRef>();
      if (uniqueMemberIds.length === 0) return map;

      const { data, error } = await supabase
        .from("workspace_members")
        .select("id, display_name")
        .in("id", uniqueMemberIds);

      if (error) {
        console.error("❌ Erro ao carregar membros:", error);
        return map;
      }

      for (const member of data ?? []) {
        if (!member.id) continue;
        map.set(member.id, { display_name: member.display_name ?? null });
      }

      return map;
    })(),
  ]);

  return { reactionsByMessageId, membersById };
}

export function collectMemberIdsFromMessages(
  messages: ChatMessage[],
): string[] {
  const ids = new Set<string>();
  for (const message of messages) {
    if (message.sent_by_member_id) ids.add(message.sent_by_member_id);
    if (message.deleted_by_member_id) ids.add(message.deleted_by_member_id);
  }
  return [...ids];
}
